// src/containers/reportBuilder/CustomSectionSlot.jsx
//
// Unified replacement for the old paired "Add text (optional)" / "Add
// graphic (optional)" insertion points scattered through the single-activity
// Report Builder template (ReportPreview.jsx). Each break now shows one
// single-line "Add custom section (optional)" button; clicking it opens a
// modal offering three independent, combinable pieces:
//   1. Section heading — a level-1 heading line, participates in the
//      Contents page and pagination exactly like a template heading.
//   2. Additional text — a WriterNoteHtml field (same bold/italic/list
//      toolbar as the rest of the report's rich-text fields).
//   3. Images/graphics — one or more images from the activity's Documents &
//      Media picker.
// Word (.docx) export doesn't exist yet (see the "Download Report" button
// elsewhere in ReportPreview.jsx) — no Lambda/JSON-payload pipeline is wired
// up for ANY of the report yet, so wiring this one feature to it ahead of
// the rest isn't done here. What IS deliberately in place so that future
// work doesn't have to be reverse-engineered out of this file's JSX:
//
//   - image.url is the permanent S3 object URL (sDocument.documentUrl, set
//     from the S3 upload's own `.Location` — see ProjectRepository.js /
//     OrganisationRepository.js) — not a presigned/expiring link — so a
//     docx export step can fetch the original file bytes directly, however
//     much later that runs.
//   - images arrays preserve insertion order, and are plain "insert as-is"
//     references with no crop/position state attached (unlike the cover
//     image) — the intent is that once the .docx is downloaded, the images
//     are ordinary inline Word pictures the author repositions/resizes with
//     Word's own tools, not something this app tries to lay out for them.
//   - getCustomSectionSlotOrder() below is the single source of truth for
//     the slots' real document order (matching ReportPreview.jsx's render
//     order and its tocEntries construction) — whatever eventually builds
//     the JSON payload for the Word Lambda should walk activity slots in
//     this order and, for each with `has.heading`/`has.text`/`has.images`,
//     emit a Heading 1 / paragraph / inline picture(s) at that point in the
//     template, rather than re-deriving the order from scratch.
//
// Data shape persisted per slot (customisation.customSections[slot]):
//   { has: { heading, text, images }, heading, text, images: [{documentId,url,name}] }
// `has` records which pieces were added — once added, a piece's placeholder
// (or emptied content) stays visible, matching how WriterNote never
// disappears just because its text is empty.

import React, { useState } from 'react';
import { withTranslation } from 'react-i18next';
import { Modal } from 'semantic-ui-react';
import {
  WriterNoteHtml,
  REPORT_FONT,
  REPORT_WEIGHT,
  TINT_TEMPLATE_BG,
} from '../../components/reports/ReportTemplate';
import { ReportTintContext } from '../../components/reports/ReportTintContext';

const escapeHtml = (s) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// ── "Add custom section (optional)" trigger ─────────────────────────────────
// A light Takso-orange (brand $Primary #ef8123) border, full page width —
// distinguishes this as a clickable insertion point from the plain grey
// placeholder text used for regular activity-UI form fields, which has no
// border at all.
const BUTTON_STYLE = {
  display: 'block',
  width: '100%',
  boxSizing: 'border-box',
  textAlign: 'left',
  background: 'none',
  border: '1px solid rgba(239, 129, 35, 0.5)',
  borderRadius: 4,
  padding: '8px 12px',
  margin: '8px 0',
  fontSize: REPORT_FONT.body,
  fontStyle: 'normal',
  color: '#9ca3af',
  cursor: 'pointer',
};

const CustomSectionButton = ({ t, onClick }) => (
  <button type="button" style={BUTTON_STYLE} onClick={onClick}>
    {t('report.builder.add_custom_section_optional')}
  </button>
);

// ── Choice modal ─────────────────────────────────────────────────────────────
let CustomSectionModal = ({ t, open, onCancel, onAdd }) => {
  const [opts, setOpts] = useState({
    heading: false,
    text: false,
    images: false,
  });

  const toggle = (key) => setOpts((o) => ({ ...o, [key]: !o[key] }));
  const any = opts.heading || opts.text || opts.images;

  const reset = () => setOpts({ heading: false, text: false, images: false });

  const handleAdd = () => {
    onAdd(opts);
    reset();
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const ROW_STYLE = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
  };

  return (
    <Modal open={open} onClose={handleCancel} size="tiny" closeIcon>
      <Modal.Header>{t('report.builder.add_custom_section')}</Modal.Header>
      <Modal.Content>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <label style={ROW_STYLE}>
            <input
              type="checkbox"
              checked={opts.heading}
              onChange={() => toggle('heading')}
            />
            {t('report.builder.insert_section_heading')}
          </label>
          <label style={ROW_STYLE}>
            <input
              type="checkbox"
              checked={opts.text}
              onChange={() => toggle('text')}
            />
            {t('report.builder.additional_text')}
          </label>
          <label style={ROW_STYLE}>
            <input
              type="checkbox"
              checked={opts.images}
              onChange={() => toggle('images')}
            />
            {t('report.builder.add_images_or_graphics')}
          </label>
        </div>
      </Modal.Content>
      <Modal.Actions>
        <button type="button" className="ui button" onClick={handleCancel}>
          {t('actions.cancel')}
        </button>
        <button
          type="button"
          className={`ui primary button${!any ? ' disabled' : ''}`}
          disabled={!any}
          onClick={handleAdd}
        >
          {t('actions.add')}
        </button>
      </Modal.Actions>
    </Modal>
  );
};

// ── Heading field ────────────────────────────────────────────────────────────
// H1-styled contentEditable. Empty: grey placeholder in the same Heading-1
// format. Filled: same visual as SubHeading (bold, upper-lower case, tinted)
// — a custom heading reads as a normal H2 sub-heading once text is entered.
const CustomHeading = ({ t, anchorId, value, onBlur }) => {
  const showTint = React.useContext(ReportTintContext);
  const initialTextRef = React.useRef(null);
  if (initialTextRef.current === null) {
    initialTextRef.current = value || '';
  }
  const hasContent = !!(value || '').trim();

  return (
    <div
      id={anchorId}
      contentEditable
      suppressContentEditableWarning
      dangerouslySetInnerHTML={{
        __html: escapeHtml(initialTextRef.current),
      }}
      onBlur={(e) => onBlur(e.currentTarget.innerText.trim())}
      data-placeholder={!hasContent ? 'Type section heading' : undefined}
      className={!hasContent ? 'cs-heading-empty' : undefined}
      style={{
        fontFamily: 'inherit',
        fontSize: REPORT_FONT.h2,
        fontWeight: REPORT_WEIGHT.h2,
        color: '#111827',
        marginTop: 16,
        marginBottom: 8,
        background: showTint && hasContent ? TINT_TEMPLATE_BG : 'transparent',
        borderRadius: showTint && hasContent ? 3 : 0,
        padding: showTint && hasContent ? '2px 6px' : 0,
        outline: 'none',
        cursor: 'text',
        breakAfter: 'avoid',
        pageBreakAfter: 'avoid',
      }}
      title={t('report.builder.click_to_edit_heading')}
    />
  );
};

// ── Images grid ──────────────────────────────────────────────────────────────
const ImagesBlock = ({ t, images, onAddMore, onRemove }) => (
  <div style={{ marginTop: 8, marginBottom: 8 }}>
    {images.length > 0 && (
      <div className="img-picker__grid" style={{ marginBottom: 6 }}>
        {images.map((img, idx) => (
          <div key={img.documentId ?? idx} className="img-picker__thumb">
            <img src={img.url} alt={img.name || 'Report graphic'} />
            <span className="img-picker__thumb-label">{img.name}</span>
            <button
              type="button"
              onClick={() => onRemove(idx)}
              style={{
                marginTop: 4,
                background: 'none',
                border: 'none',
                color: '#c0c0c0',
                fontSize: 11,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              {t('actions.remove')}
            </button>
          </div>
        ))}
      </div>
    )}
    <button
      type="button"
      onClick={onAddMore}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        color: '#c0c0c0',
        fontSize: 'inherit',
        cursor: 'pointer',
        fontStyle: 'italic',
        textDecoration: 'underline',
      }}
    >
      {images.length > 0
        ? t('report.builder.add_more_images')
        : t('report.builder.add_images_optional')}
    </button>
  </div>
);

// ── Main slot component ──────────────────────────────────────────────────────
// Props:
//   slot              unique key for this break point
//   data               customisation.customSections?.[slot] | undefined
//   anchorId           DOM id for the heading (TOC + pagination target)
//   onOpenModal()       open the 3-checkbox choice modal for this slot
//   onHeadingBlur(text)
//   onTextBlur(html)
//   onOpenImagePicker() open the (multi-select) image picker for this slot
//   onRemoveImage(index)
//   onRemoveSection()   delete the whole block, revert to the button
let CustomSectionSlot = ({
  t,
  slot: _slot,
  data,
  anchorId,
  onOpenModal,
  onHeadingBlur,
  onTextBlur,
  onOpenImagePicker,
  onRemoveImage,
  onRemoveSection,
}) => {
  if (!data) {
    return <CustomSectionButton t={t} onClick={onOpenModal} />;
  }

  return (
    <div>
      {data.has?.heading && (
        <CustomHeading
          t={t}
          anchorId={anchorId}
          value={data.heading}
          onBlur={onHeadingBlur}
        />
      )}
      {data.has?.text && (
        // .cs-text-field overrides the CKEditor placeholder's normal italic
        // (ReportTemplate.scss's shared .ck-placeholder rule) — once a piece
        // has actually been added here it isn't "optional" wording-wise
        // either, so the placeholder just reads "Add text", plain.
        <div className="cs-text-field">
          <WriterNoteHtml
            emptyPlaceholder={t('report.builder.add_text')}
            onBlur={onTextBlur}
          >
            {data.text}
          </WriterNoteHtml>
        </div>
      )}
      {data.has?.images && (
        <ImagesBlock
          t={t}
          images={data.images || []}
          onAddMore={onOpenImagePicker}
          onRemove={onRemoveImage}
        />
      )}
      <button
        type="button"
        onClick={onRemoveSection}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          marginTop: 2,
          marginBottom: 8,
          color: '#c0c0c0',
          fontSize: 11,
          cursor: 'pointer',
        }}
      >
        {t('report.builder.remove_custom_section')}
      </button>
    </div>
  );
};

// ── Canonical slot order ─────────────────────────────────────────────────────
// Expands to the full, ordered list of concrete slot keys for one activity's
// report — matching ReportPreview.jsx's actual render order exactly (title
// page → Executive Summary → Strategic Alignment [no slot] → Intended Impact
// [one slot per objective] → Evaluation [fast facts, methodology, one slot
// per plan/outcome] → Conclusion). A future Word-export payload builder
// should iterate this, not re-derive the order by reading JSX.
export const getCustomSectionSlotOrder = ({
  objectives,
  evaluationPlans,
} = {}) => {
  const order = [
    'title-page-above',
    'title-page-below',
    'exec-background',
    'conc-result-notes',
  ];
  (objectives || []).forEach((_, i) => order.push(`toc-${i}`));
  order.push('fastfacts-notes', 'eval-methodology-notes');
  (evaluationPlans || []).forEach((plan, pi) => {
    (plan.outcomes || []).forEach((_, oi) =>
      order.push(`analysis-notes-${pi}-${oi}`)
    );
  });
  order.push('exec-findings', 'conc-reflection', 'conc-nextsteps-notes');
  return order;
};

CustomSectionModal = withTranslation()(CustomSectionModal);
CustomSectionSlot = withTranslation()(CustomSectionSlot);

export default CustomSectionSlot;
export { CustomSectionModal };
